// Action Tracker Page - AIAG-VDA Step 6 Optimization
// Track recommended actions, assignments, due dates, and revised ratings
// ============================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CheckSquare,
    Plus,
    Search,
    RefreshCw,
    Printer,
    Target,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useAppContext } from '../contexts/AppContext';
import clsx from 'clsx';
import {
    PfmeaItem,
} from '../types';
import {
    getPfmeaItems,
    calculateRPN,
} from '../services/pfmeaService';

import { ActionWithContext } from '../types';
import ActionTrackerStats from '../components/ActionTracker/ActionTrackerStats';
import ActionTrackerList from '../components/ActionTracker/ActionTrackerList';

const PrintableActionReport: React.FC<{ actions: ActionWithContext[], projectName: string }> = ({ actions, projectName }) => {
    return (
        <div className="hidden print:block font-sans text-black bg-white p-8">
            <div className="mb-6 pb-4 border-b-2 border-black flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold uppercase tracking-tight">Action Tracker Report</h1>
                    <div className="text-sm mt-1 text-gray-600">Project: <span className="font-semibold text-black">{projectName}</span></div>
                </div>
                <div className="text-right text-xs">
                    <div>Generated: <span className="font-semibold">{new Date().toLocaleDateString()}</span></div>
                    <div>Total Actions: <span className="font-semibold">{actions.length}</span></div>
                </div>
            </div>

            <table className="w-full text-xs text-left border-collapse">
                <thead>
                    <tr className="border-b-2 border-black bg-gray-50">
                        <th className="py-2 pl-2 w-16">Status</th>
                        <th className="py-2 w-12 text-center">Pri</th>
                        <th className="py-2 w-1/4">Action Description</th>
                        <th className="py-2 w-1/5">Context (Step / Failure)</th>
                        <th className="py-2">Owner</th>
                        <th className="py-2">Due Date</th>
                        <th className="py-2 w-1/6">Notes/Results</th>
                    </tr>
                </thead>
                <tbody>
                    {actions.map((action, idx) => (
                        <tr key={idx} className="border-b border-gray-200 break-inside-avoid">
                            <td className="py-3 pl-2 align-top">
                                <span className={clsx(
                                    "px-1.5 py-0.5 rounded text-[10px] uppercase font-bold border",
                                    action.status === 'completed' ? "border-green-600 text-green-800" :
                                        (action.status === 'open' || action.status === 'in-progress') && new Date(action.targetDate) < new Date() ? "border-red-600 text-red-800" :
                                            "border-gray-400 text-gray-800"
                                )}>
                                    {(action.status === 'open' || action.status === 'in-progress') && new Date(action.targetDate) < new Date() ? 'OVERDUE' : action.status}
                                </span>
                            </td>
                            <td className="py-3 align-top text-center font-bold">
                                {action.pfmeaItem.actionPriority}
                            </td>
                            <td className="py-3 align-top pr-2">
                                <div className="font-semibold">{action.description}</div>
                            </td>
                            <td className="py-3 align-top pr-2">
                                <div className="text-gray-600">
                                    <span className="font-mono text-[10px]">{action.pfmeaItem.processStepNumber}</span><br />
                                    {action.pfmeaItem.failureMode}
                                </div>
                            </td>
                            <td className="py-3 align-top">{action.responsiblePerson || '-'}</td>
                            <td className="py-3 align-top">{action.targetDate ? new Date(action.targetDate).toLocaleDateString() : '-'}</td>
                            <td className="py-3 align-top text-gray-600 italic">
                                {action.notes || '-'}
                                {action.revisedRPN !== undefined && (
                                    <div className="mt-1 not-italic font-semibold text-green-700">
                                        RPN Reduced: {action.originalRPN} → {action.revisedRPN}
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="mt-8 pt-4 border-t border-gray-300 text-[10px] text-gray-500 flex justify-between">
                <span>Magnus PFMEA System</span>
                <span>Confidential - For Internal Use Only</span>
            </div>
        </div>
    );
};

const ActionTrackerPage: React.FC = () => {
    const { theme } = useTheme();
    const { hasPermission } = useAuth();
    const { activeProject, activeProjectName } = useAppContext();
    const navigate = useNavigate();
    const isDark = theme === 'dark';

    // State
    const [items, setItems] = useState<PfmeaItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState<'all' | 'open' | 'overdue' | 'completed'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

    const loadData = useCallback(async () => {
        if (!activeProjectName) return;
        setLoading(true);
        try {
            const data = await getPfmeaItems(activeProjectName);
            setItems(data);
        } catch (err: unknown) {
            console.error('Error loading items:', err);
            // Use empty array for now
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [activeProjectName]);

    // Load data
    useEffect(() => {
        if (activeProjectName) {
            loadData();
        }
    }, [activeProjectName, loadData]);

    // Extract all actions with context
    const allActions = useMemo((): ActionWithContext[] => {
        const actions: ActionWithContext[] = [];
        items.forEach(item => {
            if (item.recommendedActions && item.recommendedActions.length > 0) {
                item.recommendedActions.forEach(action => {
                    const originalRPN = calculateRPN(item.severity, item.occurrence, item.detection);
                    const revisedRPN = action.revisedSeverity && action.revisedOccurrence && action.revisedDetection
                        ? calculateRPN(action.revisedSeverity, action.revisedOccurrence, action.revisedDetection)
                        : undefined;
                    actions.push({
                        ...action,
                        pfmeaItem: item,
                        originalRPN,
                        revisedRPN,
                    });
                });
            }
        });
        return actions;
    }, [items]);

    // Filter actions
    const filteredActions = useMemo(() => {
        let filtered = allActions;

        // Status filter
        const today = new Date().toISOString().split('T')[0];
        switch (activeFilter) {
            case 'open':
                filtered = filtered.filter(a => a.status === 'open' || a.status === 'in-progress');
                break;
            case 'overdue':
                filtered = filtered.filter(a =>
                    (a.status === 'open' || a.status === 'in-progress') &&
                    a.targetDate < today
                );
                break;
            case 'completed':
                filtered = filtered.filter(a => a.status === 'completed');
                break;
        }

        // Search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(a =>
                a.description.toLowerCase().includes(term) ||
                a.responsiblePerson.toLowerCase().includes(term) ||
                a.pfmeaItem.failureMode.toLowerCase().includes(term) ||
                a.pfmeaItem.processStepName.toLowerCase().includes(term)
            );
        }

        return filtered;
    }, [allActions, activeFilter, searchTerm]);

    // Stats
    const stats = useMemo(() => {
        const today = new Date().toISOString().split('T')[0];
        const open = allActions.filter(a => a.status === 'open' || a.status === 'in-progress');
        const overdue = open.filter(a => a.targetDate < today);
        const completed = allActions.filter(a => a.status === 'completed');

        // Calculate risk reduction
        let totalOriginalRPN = 0;
        let totalRevisedRPN = 0;
        let actionsWithRevised = 0;

        allActions.forEach(a => {
            if (a.revisedRPN !== undefined) {
                totalOriginalRPN += a.originalRPN;
                totalRevisedRPN += a.revisedRPN;
                actionsWithRevised++;
            }
        });

        const riskReduction = actionsWithRevised > 0
            ? Math.round(((totalOriginalRPN - totalRevisedRPN) / totalOriginalRPN) * 100)
            : 0;

        return {
            total: allActions.length,
            open: open.length,
            overdue: overdue.length,
            completed: completed.length,
            riskReduction,
        };
    }, [allActions]);



    const handlePrint = () => {
        window.print();
    };

    // No project selected
    if (!activeProject || activeProject === 'Select Project') {
        return (
            <div className="flex flex-col items-center justify-center h-96 text-center">
                <CheckSquare size={64} className="text-slate-300 mb-4" />
                <h2 className={clsx('text-xl font-semibold mb-2', isDark ? 'text-white' : 'text-slate-800')}>
                    No Project Selected
                </h2>
                <p className="text-slate-500 mb-4">Select a project to view actions.</p>
                <button
                    onClick={() => navigate('/projects')}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                >
                    Go to Projects
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-6 print:hidden">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <h1 className={clsx('text-2xl font-bold flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                            <CheckSquare className="text-primary-500" />
                            Action Tracker
                        </h1>
                        <p className="text-slate-500 text-sm">
                            AIAG-VDA Step 6: Optimization · Track actions & revised ratings · {activeProject}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={handlePrint}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600"
                        >
                            <Printer size={16} />
                            Print Report
                        </button>
                        <button
                            onClick={loadData}
                            disabled={loading}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600"
                        >
                            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                            Refresh
                        </button>
                        {hasPermission('pfmea.edit') && (
                            <button
                                onClick={() => navigate('/pfmea-editor')}
                                className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                            >
                                <Plus size={16} />
                                Add Action
                            </button>
                        )}
                    </div>
                </div>

                {/* Stats Cards */}
                <ActionTrackerStats stats={stats} isDark={isDark} />

                {/* Filters & Search */}
                <div className={clsx(
                    'p-4 rounded-xl border flex flex-wrap items-center gap-4',
                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                )}>
                    {/* Status Tabs */}
                    <div className="flex rounded-lg bg-slate-100 dark:bg-slate-700 p-1">
                        {[
                            { key: 'all', label: 'All', count: stats.total },
                            { key: 'open', label: 'Open', count: stats.open },
                            { key: 'overdue', label: 'Overdue', count: stats.overdue },
                            { key: 'completed', label: 'Done', count: stats.completed },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveFilter(tab.key as 'all' | 'open' | 'overdue' | 'completed')}
                                className={clsx(
                                    'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                                    activeFilter === tab.key
                                        ? 'bg-white dark:bg-slate-600 shadow'
                                        : 'text-slate-500 hover:text-slate-700'
                                )}
                            >
                                {tab.label} ({tab.count})
                            </button>
                        ))}
                    </div>

                    {/* Search */}
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search actions, owners, failure modes..."
                                className={clsx(
                                    'w-full pl-9 pr-4 py-2 rounded-lg border text-sm',
                                    isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-200'
                                )}
                            />
                        </div>
                    </div>
                </div>

                {/* Actions List */}
                <ActionTrackerList
                    actions={filteredActions}
                    isDark={isDark}
                    loading={loading}
                    allActionsCount={allActions.length}
                    expandedItems={expandedItems}
                    toggleExpand={(id) => {
                        const next = new Set(expandedItems);
                        if (next.has(id)) {
                            next.delete(id);
                        } else {
                            next.add(id);
                        }
                        setExpandedItems(next);
                    }}
                    onEdit={() => navigate('/pfmea-editor')}
                    hasPermission={hasPermission('pfmea.edit')}
                />

                {/* Sample Actions for Demo (if no real data) */}
                {!loading && allActions.length === 0 && (
                    <div className={clsx(
                        'p-6 rounded-xl border',
                        isDark ? 'bg-slate-800 border-slate-700' : 'bg-amber-50 border-amber-200'
                    )}>
                        <h3 className={clsx('font-semibold flex items-center gap-2 mb-4', isDark ? 'text-white' : 'text-slate-800')}>
                            <Target size={18} className="text-amber-500" />
                            How Actions Work
                        </h3>
                        <div className="grid md:grid-cols-3 gap-4 text-sm">
                            <div className={clsx('p-4 rounded-lg', isDark ? 'bg-slate-700' : 'bg-white')}>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="w-6 h-6 rounded-full bg-primary-500 text-white text-xs flex items-center justify-center">1</span>
                                    <span className="font-medium">Identify High Risk</span>
                                </div>
                                <p className="text-slate-500">Items with H or M action priority need recommended actions</p>
                            </div>
                            <div className={clsx('p-4 rounded-lg', isDark ? 'bg-slate-700' : 'bg-white')}>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="w-6 h-6 rounded-full bg-primary-500 text-white text-xs flex items-center justify-center">2</span>
                                    <span className="font-medium">Assign & Track</span>
                                </div>
                                <p className="text-slate-500">Set owner, due date, and monitor progress</p>
                            </div>
                            <div className={clsx('p-4 rounded-lg', isDark ? 'bg-slate-700' : 'bg-white')}>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="w-6 h-6 rounded-full bg-primary-500 text-white text-xs flex items-center justify-center">3</span>
                                    <span className="font-medium">Verify & Close</span>
                                </div>
                                <p className="text-slate-500">Enter revised S/O/D ratings after action is complete</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <PrintableActionReport actions={filteredActions} projectName={activeProjectName || ''} />
        </>
    );
};

export default ActionTrackerPage;
